/* eslint-disable no-unsafe-optional-chaining */
import _ from 'lodash';
import qs from 'query-string';
import { path as ramdaPath, last, filter } from 'ramda';
import { toKebabCase, getRuntimeEnvVariables } from '../../helpers/Utils';

export function comparablesMappingHandler(comparable) {
  const { prefix, displayValue, title } = comparable;
  return `${prefix}${displayValue} ${title.display}`;
}

export function productListMappingHandler(productInfo, list) {
  return productInfo && (productInfo || []).map(data => (Object.assign({ list: list || '' }, data)));
}

export function filterDataMappingHandler(filterValues, filterSortby) {
  const {
    minAnnualSalary,
    minMonthlySalary,
    loanAmount,
    needs,
    providers,
    repaymentPeriodM,
    repaymentPeriodY,
  } = filterValues;

  let finalNeeds = needs;
  if (needs && !Array.isArray(needs)) {
    finalNeeds = [needs];
  }
  let finalProviders = providers;
  if (providers && !Array.isArray(providers)) {
    finalProviders = [providers];
  }

  const filterAmount = minMonthlySalary || minAnnualSalary || loanAmount || '';
  return [_.pickBy({
    filterType: '',
    filterAmount,
    filterNeed: finalNeeds ? finalNeeds.join('|') : '',
    filterPeriod: repaymentPeriodM || repaymentPeriodY || '',
    filterProvider: (finalProviders) ? finalProviders.join('|') : '',
    filterSortby: filterSortby || '',
  }, _.identity)];
}

export function mortgageFilterDataMappingHandler(filterValues) {
  const {
    mortgageType,
    propertyType,
    propertyValue,
    borrowPercentage,
    loanAmount,
    loanTenure,
    incomeEarntHK,
  } = filterValues;

  return [_.pickBy({
    mortgageType: mortgageType || '',
    mortgageAmount: loanAmount || '',
    mortgagePeriod: loanTenure || '',
    mortgagePropertyType: propertyType || '',
    mortgagePropertyValue: propertyValue || '',
    mortgageborrowPercentage: borrowPercentage || '',
    mortgageIncome: incomeEarntHK || false,
  }, _.identity)];
}

export function productKeyMappingHandler(productInfo, key) {
  if (!productInfo && !key) return false;
  const {
    productName,
    promotional,
    __typename,
    comparables,
    provider,
    title,
    ctaButton,
  } = productInfo;

  const { type } = promotional || {};
  const {
    displayValue,
    prefix, __typename: comparablesTypename,
  } = (comparables) ? _.last(comparables) : {};
  const productDetails = (comparables) ? comparables.map(comparablesMappingHandler).join('|') : '';
  let price = '';
  let currency = '';
  if (comparablesTypename === 'CompareElementPrice') {
    price = displayValue;
    currency = prefix;
  }

  const mappedProductInfo = {
    name: productName,
    id: title || '',
    price,
    currency,
    brand: (provider) ? provider.providerName : '',
    category: __typename,
    purchaseIntent: '',
    promotion: (type) ? type.map(t => t.key).join('|') : '',
    promoLabel: (type) ? type.map(t => t.display).join('|') : '',
    dealStatus: ctaButton?.show ? 'true' : 'false',
    productDetails,
    position: key + 1,
  };
  return mappedProductInfo;
}

export function personaLoanTrackDataMapping(formState) {
  const {
    additionalInfo,
    contactDetails,
    employmentDetails,
    personalDetails,
  } = formState || {};
  const { loanAmount, loanType, repaymentPeriod: loanPeriod } = personalDetails || {};
  const {
    employmentStatus: loanEmploymentStatus,
    typeOfIndustry: loanTypeofIndustry,
    occupation: loanOccupation,
  } = contactDetails || {};
  const { loanPurpose, residentialStatus: loanResidentialStatus } = additionalInfo || {};
  const {
    existingBankruptcyRecord: loanBankruptcy,
    doYouHaveAnIVARecord: loanIVA,
  } = employmentDetails || {};
  const data = {
    loanAmount: (loanAmount) ? loanAmount.value : '',
    loanPeriod: (loanPeriod) ? loanPeriod.value : '',
    loanType: (loanType) ? loanType.value : '',
    loanEmploymentStatus: (loanEmploymentStatus) ? loanEmploymentStatus.value : '',
    loanTypeofIndustry: (loanTypeofIndustry) ? loanTypeofIndustry.value : '',
    loanOccupation: (loanOccupation) ? loanOccupation.value : '',
    loanPurpose: (loanPurpose) ? loanPurpose.value : '',
    loanResidentialStatus: (loanResidentialStatus) ? loanResidentialStatus.value : '',
    loanBankruptcy: (loanBankruptcy) ? loanBankruptcy.value : '',
    loanIVA: (loanIVA) ? loanIVA.value : '',
  };
  return _.pickBy(data, _.identity);
}

export function applicationFormTrack(props, state) {
  if (!props && !state) return false;
  const {
    track,
    product: checkOutProduct,
    route,
    criteria,
    productId,
    path,
    currencyCode,
  } = props;

  const isTI = (route === 'travel-insurance');
  const isHL = (route === 'mortgage');
  const isPL = (route === 'personal-loan');

  const { pageId, formState } = state;
  const {
    coverage,
    premium,
    productName,
    provider,
  } = checkOutProduct;

  const STEP_ENUM = {
    MY_DETAILS: 'myDetails',
    TRAVELLER_DETAILS: 'travellerDetails',
    REVIEW: 'review',
    PAYMENT: 'payment',
    CONFIRMATION: 'confirmation',
    SUCCESS: 'success',
    COMPLETE: 'applicationComplete',
  };

  const {
    borrowPercentage,
    incomeEarntHK,
    loanAmount,
    loanTenure,
    mortgageType,
    propertyType,
    propertyValue,
  } = criteria || {};

  const id = coverage ? coverage.title : productId;
  const name = productName || '';
  const brand = provider ? provider.providerName : '';
  const price = premium ? premium.totalDisplayPrice : '';
  const category = route || '';

  let actionField = {};
  switch (pageId) {
    case STEP_ENUM.MY_DETAILS:
      actionField = { step: 1 };
      break;
    case STEP_ENUM.TRAVELLER_DETAILS:
    case STEP_ENUM.CONFIRMATION:
      actionField = { step: 2 };
      break;
    case STEP_ENUM.REVIEW:
    case STEP_ENUM.COMPLETE:
      actionField = { step: 3 };
      break;
    case STEP_ENUM.PAYMENT:
      actionField = { step: 4 };
      break;
    case STEP_ENUM.SUCCESS:
      actionField = { step: 5 };
      break;
    default: break;
  }
  const products = [];
  const commonCheckOutData = {
    name,
    id,
    brand,
    price,
    category,
  };

  const virtualPageData = {
    virtualPageURL: (path) ? `${path}/${pageId}` : '',
    providerName: brand,
    product: name,
    sku: id,
  };

  const mortgageDetail = {
    borrowPercentage,
    incomeEarntHK,
    loanAmount,
    loanTenure,
    mortgageType,
    propertyType,
    propertyValue,
  };

  if (!isTI) delete commonCheckOutData.price;
  const personaLoanDetail = Object.assign({},
    (isPL) ? personaLoanTrackDataMapping(formState) : {});

  const productDetail = Object.assign({},
    (isTI) ? commonCheckOutData : virtualPageData,
    (isHL) ? mortgageDetail : {},
    (isPL) ? personaLoanDetail : {});
  products.push(productDetail);

  const event = (isTI) ? 'checkout' : 'virtualPage';
  const checkout = Object.assign({}, { actionField }, { products });
  const ecommerce = Object.assign({}, { checkout }, isTI && { currencyCode: currencyCode || '' });
  const trackingData = Object.assign({}, (isTI) ? { ecommerce } : productDetail, { event });

  track({ data: trackingData });
  return trackingData;
}

export function postPaymentPageTrack (props) {
  if (!props) return false;
  const {
    location,
    track,
    currencyCode,
    vertical,
    verticalText,
    dataLocation,
    status,
  } = props;
  let trackObj = {
    event: 'purchase',
  };
  if (location) {
    if (location.search) {
      const search = qs.parse(location.search);
      const {
        transactionNumber, productId, productCode, totalPrice, provider, planType
      } = search;
      switch (vertical) {
        case 'PA':
          if (transactionNumber && productCode && totalPrice) {
            const revenue = Number(totalPrice) || 0;
            const actionField = {
              id: transactionNumber,
              status,
              revenue,
            };
            const products = [];
            const productDetail = {
              category: verticalText,
              id: productCode,
              price: revenue,
            };
            products.push(productDetail);
            const purchase = Object.assign({}, { actionField }, { products });
            const ecommerce = Object.assign({}, { purchase }, { currencyCode });
            const trackingData = Object.assign({}, { ecommerce }, { location: dataLocation });
            trackObj = {
              ...trackObj,
              ...trackingData,
            };

            track({
              data: {
                ...trackObj,
              },
            });
            return trackObj;
          }
          break;
        case 'TI':
          if (transactionNumber && productId && totalPrice && provider && planType) {
            const revenue = Number(totalPrice) || 0;
            const actionField = {
              id: transactionNumber,
              status,
              revenue,
            };
            const products = [];
            const productDetail = {
              category: verticalText,
              id: productId,
              price: revenue,
              provider: provider,
              planType: planType,
              quantity: 1,
              /**
               * TODO: Missing params,
               *  pending until backend GraphQL supports getting belows params based on ProductID
               * brand: Provider,
               * name: ProductName
               */
            };
            products.push(productDetail);
            const purchase = Object.assign({}, { actionField }, { products });
            const ecommerce = Object.assign({}, { purchase }, { currencyCode });
            const trackingData = Object.assign({}, { ecommerce }, { location: dataLocation });
            trackObj = {
              ...trackObj,
              ...trackingData,
            };

            track({
              data: {
                ...trackObj,
              },
            });
            return trackObj;
          }
          break;
        default:
          break;
      }
    }
  }

  return false;
}

export function contentGroupingsTrack(track, page) {
  if (!track || !page) return false;

  const pageDictionary = {
    home: 'homepage',
    result: 'result page',
    product: 'product page',
    provider: 'provider page',
    search: 'search page',
    application: 'application page',
    blog: 'blog page',
    support: 'support page',
    widget: 'affiliate widget',
    static: 'static page',
  };
  const location = pageDictionary[page];

  track({ data: { location } });
  return location && { location };
}

export function startRateTrack(track, starRate, extraAnalyticsParams) {
  const event = 'starRating';
  const rating = _.pull(starRate, false).length;
  const data = Object.assign({}, { event }, { rating }, { ...extraAnalyticsParams });
  track({ data });
}

export function resultPageTooltipClick(track, params = {}) {
  if (!track) {
    return false;
  }
  const trackingData = {
    event: 'productPanelClick',
    location: 'result page',
    componentType: 'Tooltip',
    ...params,
  };
  track({
    data: trackingData,
  });
  return trackingData;
}

export function widgetLoadTrack(track, affiliateName) {
  if (!track || !affiliateName) {
    return false;
  }
  const trackingData = {
    location: 'affiliate widget',
    details: [
      {
        affiliateName,
        affiliateId: affiliateName,
      },
    ],
  };
  track({ data: trackingData });
  return trackingData;
}

export function getBaseURL() {
  const origin = '';
  const href = window.location.href.toLowerCase();
  if (href.indexOf(`${origin}/zh`) !== -1) {
    return `${origin}/zh`;
  }
  if (href.indexOf(`${origin}/en`) !== -1) {
    return `${origin}/en`;
  }
  return origin;
}

function getLoginBaseTrackingData() {
  return {};
}

export const LOCATIONS = {
  SIGNUP: 'login - signup page',
  LOGIN: 'login - login page',
  SKIP_LOGIN_OR_SIGNUP: 'login - skip login/signup page',
  FORGOT_PASSWORD: 'login - forgot password page',
  DASHBOARD: 'login - dashboard page',
  DASHBOARD_WHITEPAPER: 'login - whitepaper',
  SETTING: 'login - settings page',
  '2FA': 'login - 2FA SMS page',
  REWARD_PASSWORD: 'login - rewards passcode page',
  CC_DETAILS: 'login - CC Details page',
  PL_DETAILS: 'login - PL Details page',
  TI_DETAILS: 'login - TI Details page',
  PA_DETAILS: 'login - PA Details page',
  OB_DETAILS: 'login - OB Details page',
  CAMPAIGN_PAGE: 'campaign page',
  REWARDS_LANDING_PAGE: 'rewards page', // special value naming requirement
  REWARDS_PAGE: 'login - rewards page',
  REWARDS_REDEMPTION_PAGE: 'login - rewards redemption page',
  ERROR: 'error page',
};

export const VIRTUAL_PAGES_URL = {
  REWARDS_FORM_SUCCESS: 'reward-form-success',
  REWARDS_THANK_YOU: 'reward-thank-you',
  REWARDS_SKIP: 'reward-skip',
};

export const VERTICAL_CODE_FROM_PRODUCT_TYPENAME = {
  CustomerPaProduct: 'PA',
  CustomerObProduct: 'OB',
  PersonalLoan: 'PL',
  CreditCard: 'CC',
};

const STATUS = {
  SUCCESS: 'success',
  FAILURE: 'failure',
};

function loginEventTrack(track, extraData) {
  if (!track) {
    return false;
  }
  const trackingData = {
    ...getLoginBaseTrackingData(),
    ...extraData,
  };
  track({ data: trackingData });
  return trackingData;
}

export function getValueFromStatus(status) {
  if (status === 'error' || status === STATUS.FAILURE || status === false) {
    return STATUS.FAILURE;
  }
  if (status === STATUS.SUCCESS || status === true) {
    return STATUS.SUCCESS;
  }
  return '';
}

// All the page tracking within the lightbox
export function loginLightboxLoadTrack(track, page, loanRecommend, prevPage) {
  if (!track) {
    return false;
  }
  let data = getLoginBaseTrackingData();

  switch (page) {
    case 'SIGNUP':
      data = { ...data, location: LOCATIONS.SIGNUP, virtualPageURL: `${getBaseURL()}/signup` };
      if (loanRecommend) {
        data = { ...data, verticalCode: 'PL' };
      }
      break;
    case 'REWARDS_REGISTER':
      data = { ...data, location: LOCATIONS.SIGNUP, virtualPageURL: `${getBaseURL()}/signup` };
      break;
    case 'EMAIL_SIGNUP':
      data = { ...data, location: LOCATIONS.SIGNUP, virtualPageURL: `${getBaseURL()}/signup/email-step1` };
      break;
    case 'MFA_REGISTRATION':
      data = { ...data, location: LOCATIONS.SIGNUP, virtualPageURL: `${getBaseURL()}/signup/phone-registration` };
      break;
    case 'MFA_ACTIVATION':
      data = { ...data, location: LOCATIONS.SIGNUP, virtualPageURL: `${getBaseURL()}/signup/2fa-sms` };
      break;
    case 'EMAIL_REGISTRATION':
      data = { ...data, location: LOCATIONS.SIGNUP, virtualPageURL: `${getBaseURL()}/signup/facebook-whats-your-email` };
      break;
    case 'MFA_VERIFICATION':
      data = { ...data, location: LOCATIONS.SIGNUP, virtualPageURL: `${getBaseURL()}/login/2fa-sms` };
      break;
    case 'LOGIN':
      data = { ...data, location: LOCATIONS.LOGIN, virtualPageURL: `${getBaseURL()}/login` };
      if (loanRecommend) {
        data = {
          ...data, event: 'virtualPage', verticalCode: 'PL',
        };
        (async () => {
          try {
            const res = await fetch(getRuntimeEnvVariables('RUNTIME_CLIENT_COOKIE_SERVER'), { credentials: 'include' });
            const { instance, session, customer_id: CAGID } = await res.json();
            const trackingData = {
              data: {
                instance, session, CAGID, ...data,
              },
            };
            track(trackingData);
          } catch (e) {
            // console.error(e);
          }
        })();
        return { data: { ...data } };
      }
      break;
    case 'SKIP_LOGIN_OR_SIGNUP': {
      let virtualPageUrl = '';
      if (prevPage === 'LOGIN') {
        virtualPageUrl = `${getBaseURL()}/login/skip-confirmation`;
      } else if (prevPage === 'SIGNUP') {
        virtualPageUrl = `${getBaseURL()}/signup/skip-confirmation`;
      }
      data = {
        ...data, location: 'journey abandonment', virtualPageURL: virtualPageUrl, verticalCode: 'PL',
      };
      break;
    }
    case 'RESET_PASSWORD':
      data = { ...data, location: LOCATIONS.FORGOT_PASSWORD, virtualPageURL: `${getBaseURL()}/forgot-password-enter-email` };
      break;
    case 'EMAIL_VERIFICATION':
      data = { ...data, location: LOCATIONS.FORGOT_PASSWORD, virtualPageURL: `${getBaseURL()}/forgot-password-check-inbox` };
      break;
    case 'CREATE_NEW_PASSWORD':
      data = { ...data, location: LOCATIONS.FORGOT_PASSWORD, virtualPageURL: `${getBaseURL()}/forgot-password-create-password` };
      break;
    case 'FORGOT_EXPIRED_LINK':
      data = { ...data, location: LOCATIONS.FORGOT_PASSWORD, virtualPageURL: `${getBaseURL()}/forgot-password-expired-link` };
      break;
    /* This page do not exist, for report building only */
    case 'LOGIN_SUCCESS':
      data = { ...data, location: LOCATIONS.LOGIN, virtualPageURL: `${getBaseURL()}/login/success` };
      break;
    /* This page do not exist, for report building only */
    case 'SIGNUP_COMPLETE':
      data = { ...data, location: LOCATIONS.SIGNUP, virtualPageURL: `${getBaseURL()}/signup/complete` };
      break;
    default:
      console.log(`Missing page tracking for the page ${page}`);
  }
  const trackingData = {
    event: 'virtualPage',
    ...data,
  };
  track({ data: trackingData });
  return trackingData;
}

export function goToSkipLoginSignupEventTrack(track, location, staticText) {
  if (!track) {
    return false;
  }
  let virtualPageURL = '';
  let buttonText = '';
  let title = '';
  if (location === 'LOGIN') {
    virtualPageURL = `${getBaseURL() }/login`;
    buttonText = staticText.SKIP_LOGIN_TEXT_LINK;
    title = 'skipLogin';
  } else if (location === 'SIGNUP') {
    virtualPageURL = `${getBaseURL() }/signup`;
    buttonText = staticText.SKIP_SIGNUP_TEXT_LINK;
    title = 'skipSignup';
  }
  const extraData = {
    event: 'login_navigate',
    location: LOCATIONS[location],
    virtualPageURL,
    buttonName: buttonText,
    componentType: 'pop-up',
    action: 'click',
    title: title,
    verticalCode: 'PL',
  };
  return loginEventTrack(track, extraData);
}

export function confirmSkipLoginSignupEventTrack(track, location, staticText) {
  if (!track) {
    return false;
  }
  let virtualPageURL = '';
  let title = '';
  if (location === 'LOGIN') {
    virtualPageURL = `${getBaseURL() }/login/skip-confirmation`;
    title = 'confirmSkipLogin';
  } else if (location === 'SIGNUP') {
    virtualPageURL = `${getBaseURL() }/signup/skip-confirmation`;
    title = 'confirmSkipSignup';
  }
  const extraData = {
    event: 'login_navigate',
    location: 'journey abandonment',
    virtualPageURL,
    buttonName: staticText.CONFIRM_SKIP_LOGIN_SIGNUP,
    componentType: 'pop-up',
    action: 'click',
    title: title,
    verticalCode: 'PL',
  };
  return loginEventTrack(track, extraData);
}

export function returnToPreviousLocationFromSkipEventTrack(track, location, staticText) {
  if (!track) {
    return false;
  }
  let virtualPageURL = '';
  let title = '';
  if (location === 'LOGIN') {
    virtualPageURL = `${getBaseURL() }/login/skip-confirmation`;
    title = 'returnLogin';
  } else if (location === 'SIGNUP') {
    virtualPageURL = `${getBaseURL() }/signup/skip-confirmation`;
    title = 'returnSignup';
  }
  const extraData = {
    event: 'login_navigate',
    location: 'journey abandonment',
    virtualPageURL,
    buttonName: staticText.BACK_TO_LOGIN_SIGNUP,
    componentType: 'pop-up',
    action: 'click',
    title,
    verticalCode: 'PL',
  };
  return loginEventTrack(track, extraData);
}

// Click on either Facebook/Google/Email signup
export function signupPopupLoginSignupBeginEvent(track, media) {
  if (!media) {
    return false;
  }
  const extraData = {
    location: LOCATIONS.SIGNUP,
    intent: 'enter email',
    method: media,
    event: 'login_signup_begin',
  };
  return loginEventTrack(track, extraData);
}

// User enters into email input field and validation is correct
export function signupPopupLoginSignupEnterEmailEvent(track, status) {
  if (typeof status === 'undefined') {
    return false;
  }
  const extraData = {
    location: LOCATIONS.SIGNUP,
    intent: 'enter email',
    status: getValueFromStatus(status),
    event: 'login_signup_enter_email',
  };
  return loginEventTrack(track, extraData);
}

// FE validation
export function signupPopupLoginSignupEnterPasswordEvent(track, status) {
  if (typeof status === 'undefined') {
    return false;
  }
  const extraData = {
    location: LOCATIONS.SIGNUP,
    intent: 'enter password',
    status: getValueFromStatus(status),
    event: 'login_signup_enter_password',
  };
  return loginEventTrack(track, extraData);
}

// FE validation
export function signupPopupLoginSignupConfirmPasswordEvent(track, status) {
  if (typeof status === 'undefined') {
    return false;
  }
  const extraData = {
    location: LOCATIONS.SIGNUP,
    intent: 'confirm password',
    status: getValueFromStatus(status),
    event: 'login_signup_confirm_password',
  };
  return loginEventTrack(track, extraData);
}

// When the sign up form is successfully submitted
export function signupPopupLoginSignupStep1SubmissionEvent(track, data) {
  const extraData = {
    location: LOCATIONS.SIGNUP,
    intent: 'step1 submission',
    status: getValueFromStatus(data.feedback === 'REQUIRED'),
    event: 'login_signup_step1_submission',
  };
  return loginEventTrack(track, extraData);
}

// Registering a phone number during sign up as step 2
export function signupPopupLoginSignupPhoneRegistrationEvent(track, data, formData) {
  let extraData = {
    location: LOCATIONS.SIGNUP,
    intent: 'phone registration',
    status: getValueFromStatus(data.feedback === 'REQUIRED'),
    event: 'login_signup_phone_registration',
  };
  if (formData && typeof formData?.checkbox === 'boolean') {
    extraData = {
      ...extraData,
      marketingConsent: formData.checkbox ? 'yes' : 'no',
    };
  }
  return loginEventTrack(track, extraData);
}

export function signupPopupLoginSignupEmailOTPEvent(track) {
  const extraData = {
    location: LOCATIONS.SIGNUP,
    intent: 'verify by email',
    virtualPageURL: `${getBaseURL()}/signup/2fa-sms`,
    event: 'login_signup_click',
  };
  return loginEventTrack(track, extraData);
}

// SMS verification code submit
export function signupPopupLoginSignupOTPVerificationEvent(track, data, isVerifyByEmail) {
  const extraData = isVerifyByEmail
  ? {
    location: LOCATIONS.SIGNUP,
    intent: 'Email 2FA',
    status: getValueFromStatus(data[`${'__'}typename`] === 'LoggedIn'),
    event: 'login_signup_click',
    virtualPageURL: `${getBaseURL()}/signup/2fa-email`,
  }
  : {
    location: LOCATIONS.SIGNUP,
    intent: 'SMS 2FA',
    status: getValueFromStatus(data[`${'__'}typename`] === 'LoggedIn'),
    event: 'login_signup_sms_verification',
  };
  return loginEventTrack(track, extraData);
}

export function signupPopupLoginSignupOTPResendEvent(track) {
  const extraData = {
    event: 'login_signup_click',
    location: LOCATIONS.SIGNUP,
    intent: 'resendOTP',
    virtualPageURL: `${getBaseURL()}/signup/2fa-email`,
  };
  return loginEventTrack(track, extraData);
}

export function signupPopupLoginSignupOTPEmailBackClickEvent(track) {
  const extraData = {
    event: 'login_signup_click',
    location: LOCATIONS.SIGNUP,
    intent: 'back',
    virtualPageURL: `${getBaseURL()}/signup/2fa-email`,
  };
  return loginEventTrack(track, extraData);
}

export function signupPopupLoginSignupOTPEmailPageCloseEvent(track) {
  const extraData = {
    event: 'login_signup_click',
    location: LOCATIONS.SIGNUP,
    intent: 'close',
    virtualPageURL: `${getBaseURL()}/signup/2fa-email`,
  };
  return loginEventTrack(track, extraData);
}

// Clicking continue/submit either successfully or failing (not pass validation).
// This only happens when a user logs in on facebook and we don't get an email from facebook's api.
export function signupPopupLoginSignupFacebookEmailregisterEvent(track, status) {
  if (typeof status === 'undefined') {
    return false;
  }
  const extraData = {
    location: LOCATIONS.SIGNUP,
    intent: 'submit email',
    method: 'facebook',
    status: getValueFromStatus(status),
    event: 'login_signup_facebook_emailregister',
  };
  return loginEventTrack(track, extraData);
}

export function cookieCagIdTrackEvent(track, callback) {
  (async () => {
    try {
      const res = await fetch(getRuntimeEnvVariables('RUNTIME_CLIENT_COOKIE_SERVER'), { credentials: 'include' });
      const { instance, session, customer_id: CAGID } = await res.json();
      if (instance && session) {
        track({ data: { instance, session, CAGID } });
        if (callback) {
          callback();
        }
      }
    } catch (e) {
      // console.error(e);
    }
  })();
}

// A user account is successfully created on back-end
export function signupPopupLoginSignupCompleteEvent(track, media, data) {
  return cookieCagIdTrackEvent(track, () => {
    loginLightboxLoadTrack(track, 'SIGNUP_COMPLETE');
    const extraData = {
      location: LOCATIONS.LOGIN,
      intent: 'signup complete',
      method: media,
      status: getValueFromStatus(data[`${'__'}typename`] === 'LoggedIn'),
      event: 'login_signup_complete',
    };
    return loginEventTrack(track, extraData);
  });
}

// When a user has successfully logged in
export function loginPopupLoginSuccessfulEvent(track) {
  return cookieCagIdTrackEvent(track, () => {
    loginLightboxLoadTrack(track, 'LOGIN_SUCCESS');
    const extraData = {
      location: LOCATIONS.LOGIN,
      intent: 'login success',
      status: STATUS.SUCCESS,
      event: 'login_successful',
    };
    return loginEventTrack(track, extraData);
  });
}

// When a user has successfully logged out
export function loginPopupLogoutSuccessfulEvent(track) {
  const extraData = {
    location: LOCATIONS.LOGIN,
    intent: 'logout success',
    status: STATUS.SUCCESS,
    event: 'logout_successful',
  };
  return loginEventTrack(track, extraData);
}

// Click on "Continue with Facebook / Google" CTA
export function loginPopupLoginSocialLoginEvent(track, socialMedia) {
  if (!socialMedia) {
    return false;
  }
  const extraData = {
    location: LOCATIONS.LOGIN,
    intent: 'login',
    method: socialMedia,
    event: 'login_social_login',
  };
  return loginEventTrack(track, extraData);
}

// Submit the login form
export function loginPopupLoginEmailLoginEvent(track) {
  const extraData = {
    method: 'email',
  };
  return loginEventTrack(track, extraData);
}

// When a user enters their email address with the wrong password 5 times,
// the FE should show the user 'Too Many Attempts' and user should be locked out.
export function loginPopupLoginHardLockEvent(track) {
  const extraData = {
    location: LOCATIONS.LOGIN,
    intent: 'password locked',
    status: STATUS.FAILURE,
    event: 'login_hard_lock',
  };
  return loginEventTrack(track, extraData);
}

// Enters email and clicks submit (successfully or unsuccessfully)
export function forgotPasswordLoginEnterEmailEvent(track, status) {
  if (typeof status === 'undefined') {
    return false;
  }
  const extraData = {
    location: LOCATIONS.FORGOT_PASSWORD,
    intent: 'enter email',
    status: getValueFromStatus(status),
    event: 'login_enter_email',
  };
  return loginEventTrack(track, extraData);
}

// Click on "Open Inbox" button
export function forgotPasswordLoginOpenInboxEvent(track) {
  const extraData = {
    location: LOCATIONS.FORGOT_PASSWORD,
    intent: 'open inbox',
    event: 'login_open_inbox',
  };
  return loginEventTrack(track, extraData);
}

// CTA & form submission for Create password. Success if form submitted
export function forgotPasswordLoginResetPasswordStep1Event(track) {
  const extraData = {
    location: LOCATIONS.FORGOT_PASSWORD,
    intent: 'create new password',
    status: STATUS.SUCCESS,
    event: 'login_reset_password_step1',
  };
  return loginEventTrack(track, extraData);
}

// SMS verification code submit
export function loginVerificationEvent(track, data, isVerifyByEmail) {
  const extraData = isVerifyByEmail ? {
    location: LOCATIONS.SIGNUP,
    intent: 'Email 2FA',
    status: getValueFromStatus(data[`${'__'}typename`] === 'LoggedIn'),
    event: 'login_signup_click',
    virtualPageURL: `${getBaseURL()}/signup/2fa-email`,
  }
  : {
    location: LOCATIONS['2FA'],
    intent: 'SMS 2FA',
    status: getValueFromStatus(data[`${'__'}typename`] === 'LoggedIn'),
    event: 'login_sms_verification',
  };
  return loginEventTrack(track, extraData);
}

// When a user clicks an active CTA and saves changes on fields that they have correctly edited
export function userProfileLoginDashboardSaveProfileChangesEvent(track, status) {
  const extraData = {
    location: LOCATIONS.DASHBOARD,
    status: getValueFromStatus(status),
    intent: 'save profile changes',
    event: 'login_dashboard_save_profile_changes',
  };
  return loginEventTrack(track, extraData);
}

// When a user clicks on the anchor, send the name of the module (Products or Rewards)
export function dashboardLoginDashboardAnchorClickEvent(track, module) {
  const extraData = {
    location: LOCATIONS.DASHBOARD,
    intent: `${module} module click`,
    event: 'login_dashboard_anchor_click',
  };
  return loginEventTrack(track, extraData);
}

// When a user clicks View All
export function dashboardLoginDashboardViewallEvent(track, module) {
  const extraData = {
    location: LOCATIONS.DASHBOARD,
    intent: 'view all',
    dashboardModule: `all ${module}`,
    event: 'login_profile_view_all',
  };
  return loginEventTrack(track, extraData);
}

// When a user clicks on the CTA in the promo banner on the dashboard
export function dashboardPromoBannerCtaClickEvent(track) {
  const extraData = {
    location: LOCATIONS.DASHBOARD,
    intent: 'banner redeem now click',
    event: 'login_dashboard_click',
    dashboardModule: 'promo banner',
  };
  return loginEventTrack(track, extraData);
}

// When user clicks the user profile icon in the top right hand corner (Global NavBar and Dashboard)
export function loginProfileButtonClickEvent(track) {
  const extraData = {
    intent: 'profile button click - top right corner',
    event: 'login_profile_button_click',
  };
  return loginEventTrack(track, extraData);
}

// When a user clicks to navigate to different areas of dashboard
export function dashboardLoginDashboardNavigationMenuClickEvent(track, buttonName) {
  const extraData = {
    intent: 'navigation menu click',
    event: 'login_dashboard_navigation_menu_click',
    buttonName,
    dashboardModule: 'navigation menu',
  };
  return loginEventTrack(track, extraData);
}

// Click Delete Account CTA
export function settingLoginDeleteAccountEvent(track) {
  const extraData = {
    location: LOCATIONS.SETTING,
    intent: 'delete account',
    event: 'login_delete_account',
  };
  return loginEventTrack(track, extraData);
}

// #2 Send products data
export function loginDashboardSendProducts(track, products) {
  const extraData = {
    products,
  };
  return loginEventTrack(track, extraData);
}

// #3 send rewards data
export function loginDashboardSendRewards(track, rewards) {
  const extraData = {
    rewards,
  };
  return loginEventTrack(track, extraData);
}

// #4, 11,12 PL card on list of applied products at Products Module
export function loginDashboardAllProductsModuleProductClickEvent(track, verticalCode, product) {
  if (typeof verticalCode === 'undefined' || typeof product === 'undefined') {
    return false;
  }
  let intent;
  let event;
  let vertical;

  switch (verticalCode) {
    case 'TI':
      intent = 'review policy';
      event = 'login_dashboard_reviewPolicy';
      break;
    case 'PL':
    case 'PA':
    case 'CC':
    case 'OB':
      intent = 'acquired product click';
      event = 'login_dashboard_all_products_module_product_click';
      break;
    default:
  }

  switch (verticalCode) {
    case 'OB':
      vertical = null;
      break;
    default:
      vertical = verticalCode;
  }

  const extraData = {
    location: LOCATIONS.DASHBOARD,
    intent,
    event,
    dashboardModule: 'all products',
    products: [product],
  };

  if (vertical) {
    extraData.verticalCode = vertical;
  }

  return loginEventTrack(track, extraData);
}

// #5 Click on Card CTA
export function loginDashboardCardCtaEvent(track, verticalCode, product) {
  if (typeof verticalCode === 'undefined' || typeof product === 'undefined') {
    return false;
  }
  let intent;
  let event;
  switch (verticalCode) {
    case 'TI':
      intent = 'buy another policy';
      event = 'login_dashboard_buyanother';
      break;
    case 'PA':
      intent = 'buy another policy';
      event = 'login_dashboard_buyanother';
      break;
    default:
  }
  const extraData = {
    location: LOCATIONS.DASHBOARD,
    intent,
    dashboardModule: 'all products',
    verticalCode,
    event,
    products: [product],
  };
  return loginEventTrack(track, extraData);
}

// #6, 9, 10 All products empty state icon click
export function loginDashboardAllProductsModuleCtaClickEvent(track, verticalCode) {
  if (typeof verticalCode === 'undefined') {
    return false;
  }
  let intent;
  switch (verticalCode) {
    case 'PA':
      intent = 'personal accident click';
      break;
    case 'TI':
      intent = 'travel insurance click';
      break;
    case 'CC':
      intent = 'credit card click';
      break;
    case 'PL':
      intent = 'personal loan click';
      break;
    default:
  }
  const extraData = {
    location: LOCATIONS.DASHBOARD,
    intent,
    verticalCode,
    dashboardModule: 'all products',
    event: 'login_dashboard_all_products_module_cta_click',
  };
  return loginEventTrack(track, extraData);
}

// #13 Upper part left CTA
export function loginDashboardProductDetailsClickEvent(track, verticalCode, product) {
  if (typeof verticalCode === 'undefined') {
    return false;
  }
  let location;
  let intent;
  let dashboardModule;
  let event;
  switch (verticalCode) {
    case 'PA':
      location = LOCATIONS.PA_DETAILS;
      intent = 'make a claim';
      dashboardModule = 'PA policy details';
      event = 'login_dashboard_claim';
      break;
    case 'CC':
      location = LOCATIONS.CC_DETAILS;
      intent = 'credit card product details click';
      dashboardModule = 'CC product details';
      event = 'login_dashboard_vertical_details_product_details_click';
      break;
    default:
  }
  const extraData = {
    location,
    intent,
    dashboardModule,
    verticalCode,
    products: [product],
    event,
  };
  return loginEventTrack(track, extraData);
}
// #14 #17(TI) #23(PL) Upper part right CTA
export function loginDashboardClaimRewardClickEvent(track, verticalCode, product) {
  if (typeof verticalCode === 'undefined') {
    return false;
  }
  let location;
  let intent;
  let dashboardModule;
  let event = 'login_dashboard_vertical_details_claim_reward_click';
  switch (verticalCode) {
    case 'PA':
      location = LOCATIONS.PA_DETAILS;
      intent = 'buy another policy';
      dashboardModule = 'PA policy details';
      event = 'login_dashboard_buyanother';
      break;
    case 'TI':
      location = LOCATIONS.TI_DETAILS;
      intent = 'buy another policy';
      dashboardModule = 'TI policy details';
      event = 'login_dashboard_buyanother';
      break;
    case 'CC':
      location = LOCATIONS.CC_DETAILS;
      intent = 'credit card claim reward click';
      dashboardModule = 'CC product details';
      break;
    case 'PL':
      location = LOCATIONS.PL_DETAILS;
      intent = 'peroanl loan claim reward click';
      dashboardModule = 'PL product details';
      break;
    default:
  }
  const extraData = {
    location,
    intent,
    dashboardModule,
    verticalCode,
    products: [product],
    event,
  };
  return loginEventTrack(track, extraData);
}

// #15 Tab menu details
export function loginDashboardViewDetailsEvent(track, verticalCode, product) {
  if (typeof verticalCode === 'undefined') {
    return false;
  }
  let location;
  let intent;
  let dashboardModule;
  switch (verticalCode) {
    case 'CC':
      location = LOCATIONS.CC_DETAILS;
      intent = 'view credit card details';
      dashboardModule = 'CC product details';
      break;
    default:
  }
  const extraData = {
    location,
    intent,
    dashboardModule,
    verticalCode,
    products: [product],
    event: 'login_dashboard_vertical_details_view_details',
  };
  return loginEventTrack(track, extraData);
}
// #16 Tab menu rewards
export function loginDashboardViewRewardsEvent(track, verticalCode, product) {
  if (typeof verticalCode === 'undefined') {
    return false;
  }
  let location;
  let intent;
  let dashboardModule;
  switch (verticalCode) {
    case 'CC':
      location = LOCATIONS.CC_DETAILS;
      intent = 'view credit card rewards';
      dashboardModule = 'CC product rewards';
      break;
    default:
  }
  const extraData = {
    location,
    intent,
    dashboardModule,
    verticalCode,
    products: [product],
    event: 'login_dashboard_vertical_details_view_rewards',
  };
  return loginEventTrack(track, extraData);
}

// #18 Download Icon Brochure
export function loginDashboardDownloadBrochureEvent(track, verticalCode, product) {
  if (typeof verticalCode === 'undefined') {
    return false;
  }
  const extraData = {
    location: LOCATIONS[`${verticalCode}_DETAILS`],
    intent: 'download brochure',
    dashboardModule: `${verticalCode} policy details`,
    verticalCode,
    products: [product],
    event: 'login_dashboard_downloadBrochure',
  };
  return loginEventTrack(track, extraData);
}
// #19 Download Icon Policy
export function loginDashboardPolicyWordingEvent(track, verticalCode, product) {
  if (typeof verticalCode === 'undefined') {
    return false;
  }
  const extraData = {
    location: LOCATIONS[`${verticalCode}_DETAILS`],
    intent: 'review policy wordings',
    dashboardModule: `${verticalCode} policy details`,
    verticalCode,
    products: [product],
    event: 'login_dashboard_policyWording',
  };
  return loginEventTrack(track, extraData);
}
// #19 Download icon - terms and conditions
export function loginDashboardTermsAndConditionsClickEvent(track, verticalCode, product) {
  if (typeof verticalCode === 'undefined') {
    return false;
  }
  let vertical;

  switch (verticalCode) {
    case 'OB':
      vertical = null;
      break;
    default:
      vertical = verticalCode;
  }

  const extraData = {
    location: LOCATIONS[`${verticalCode}_DETAILS`],
    intent: 'download terms and conditions',
    dashboardModule: `${verticalCode} policy details`,
    products: [product],
    event: 'login_dashboard_termsAndConditions',
  };

  if (vertical) {
    extraData.verticalCode = vertical;
  }

  return loginEventTrack(track, extraData);
}

export function loginDashboardFAQClickEvent(track, verticalCode, product) {
  if (typeof verticalCode === 'undefined') {
    return false;
  }
  let vertical;

  switch (verticalCode) {
    case 'OB':
      vertical = null;
      break;
    default:
      vertical = verticalCode;
  }

  const extraData = {
    location: LOCATIONS[`${verticalCode}_DETAILS`],
    intent: 'FAQ',
    dashboardModule: `${verticalCode} policy details`,
    products: [product],
    event: 'login_dashboard_faq',
  };

  if (vertical) {
    extraData.verticalCode = vertical;
  }

  return loginEventTrack(track, extraData);
}

// #20 Hotline
export function loginDashboardCallHotlineEvent(track, verticalCode, product) {
  if (typeof verticalCode === 'undefined') {
    return false;
  }
  const extraData = {
    location: LOCATIONS[`${verticalCode}_DETAILS`],
    intent: 'call hotline',
    dashboardModule: `${verticalCode} policy details`,
    verticalCode,
    products: [product],
    event: 'login_dashboard_callHotline',
  };
  return loginEventTrack(track, extraData);
}

// Claim Email
export function loginDashboardClaimEmailEvent(track, verticalCode, product) {
  if (typeof verticalCode === 'undefined') {
    return false;
  }
  const extraData = {
    location: LOCATIONS[`${verticalCode}_DETAILS`],
    intent: 'claim email click',
    dashboardModule: `${verticalCode} policy details`,
    verticalCode,
    products: [product],
    event: 'login_dashboard_claimEmail',
  };
  return loginEventTrack(track, extraData);
}

// Claim Form
export function loginDashboardClaimFormEvent(track, verticalCode, product) {
  if (typeof verticalCode === 'undefined') {
    return false;
  }
  const extraData = {
    location: LOCATIONS[`${verticalCode}_DETAILS`],
    intent: 'claim form click',
    dashboardModule: `${verticalCode} policy details`,
    verticalCode,
    products: [product],
    event: 'login_dashboard_claimForm',
  };
  return loginEventTrack(track, extraData);
}

// #21 Reward ticket status click
export function loginDashboardRewardsClaimNowEvent(track, product) {
  const extraData = {
    location: LOCATIONS.DASHBOARD,
    intent: 'claim rewards now',
    dashboardModule: 'rewards',
    products: [product],
    event: 'login_dashboard_rewards_claim_now',
  };
  return loginEventTrack(track, extraData);
}
// #22 Reward ticket whitespace click
export function loginDashboardRewardsDetailsEvent(track, product) {
  const extraData = {
    location: LOCATIONS.DASHBOARD,
    intent: 'view rewards details',
    dashboardModule: 'rewards',
    products: [product],
    event: 'login_dashboard_rewards_details',
  };
  return loginEventTrack(track, extraData);
}

// #24 Promotional card, product details CTA
export function loginDashboardPromotionalCard(track, {
  title, position, version, product,
}) {
  const data = {
    event: 'login_dashboard_promotional_card',
    location: LOCATIONS.DASHBOARD,
    intent: 'find out more product details',
    dashboardModule: 'promotional card',
    promotionCardName: title,
    promotionCardPosition: position + 1,
    promotionalCardVersion: version,
  };

  if (product) {
    data.products = [product];
  }

  return loginEventTrack(track, data);
}

// #25 Promotional card swipe
export function loginDashboardPromotionalCardSwipe(track, {
  title, position, product, version,
}) {
  const data = {
    event: 'login_dashboard_promotional_card_swipe',
    location: LOCATIONS.DASHBOARD,
    intent: 'find out more product details - swipe',
    dashboardModule: 'promotional card',
    promotionCardName: title,
  };

  if (position) { data.promotionCardPosition = position + 1; }
  if (version) { data.promotionalCardVersion = version; }
  if (product) { data.products = [product]; }

  return loginEventTrack(track, data);
}

// #26 Promotionals load
export function loginDashboardPromotionalCardLoad(track, { title, position, version }) {
  const data = {
    event: 'login_dashboard_promotional_card_load',
    location: LOCATIONS.DASHBOARD,
    intent: 'promotional card load',
    dashboardModule: 'promotional card',
    promotionCardName: title,
  };
  if (position) { data.promotionCardPosition = position + 1; }
  if (version) { data.promotionalCardVersion = version; }

  return loginEventTrack(track, data);
}

// #27 Content card, read article CTA
export function loginDashboardContentCard(track, {
  category, header, articleId, articleTags, position, version,
}) {
  const data = {
    event: 'login_dashboard_content_card',
    location: LOCATIONS.DASHBOARD,
    intent: 'read articles',
    dashboardModule: 'content card',
    blogCategory: category,
    blogTitle: header,
    blogArticleId: articleId,
    blogTags: articleTags,
    contentCardPosition: position + 1,
    contentCardVersion: version,
  };

  return loginEventTrack(track, data);
}

// #28 Content card swipe
export function loginDashboardContentCardSwipe(track, {
  category, header, articleId, articleTags, position, version,
}) {
  const data = {
    event: 'login_dashboard_content_card_swipe',
    location: LOCATIONS.DASHBOARD,
    intent: 'swipe - articles',
    dashboardModule: 'content card',
    blogCategory: category,
    blogTitle: header,
    blogArticleId: articleId,
    blogTags: articleTags,
    contentCardPosition: position + 1,
    contentCardVersion: version,
  };

  return loginEventTrack(track, data);
}

// #29 Content card load
export function loginDashboardContentCardLoad(track, {
  articleId, header, position, version,
}) {
  const data = {
    event: 'login_dashboard_content_card_load',
    location: LOCATIONS.DASHBOARD,
    intent: 'content card load',
    dashboardModule: 'content card',
    blogArticleId: articleId,
    blogTitle: header,
    contentCardPosition: position + 1,
    contentCardVersion: version,
  };

  return loginEventTrack(track, data);
}

// Taiwan #5
export function loginDashboardRewardsRedeemWithPasscodeEvent(track) {
  const extraData = {
    location: LOCATIONS.REWARD_PASSWORD,
    intent: 'proceed rewards redeem with passcode',
    event: 'login_dashboard_rewards_redeem_with_passcode',
    dashboardModule: 'rewards',
  };
  return loginEventTrack(track, extraData);
}

// Taiwan #6
export function loginDashboardRewardsRedeemWithPasscodeAbandonEvent(track) {
  const extraData = {
    location: LOCATIONS.REWARD_PASSWORD,
    intent: 'abandon rewards redeem with passcode',
    event: 'login_dashboard_rewards_redeem_with_passcode_abandon',
    dashboardModule: 'rewards',
  };
  return loginEventTrack(track, extraData);
}

export const widgetNavigationTrack = (track, data) => {
  if (!data) return false;
  const { componentType, action = 'click', title } = data;
  if (!componentType || !title) return false;
  const trackingData = {
    event: 'navigate',
    location: 'affiliate widget',
    componentType,
    action,
    title,
  };
  track({ data: trackingData });
  return trackingData;
};

export function widgetTabLoadTrack(track, tabTitle, products) {
  if (!track || !tabTitle || !products) {
    return false;
  }
  const impressions = products?.map((product, index) => {
    const {
      displayValue,
      prefix,
      __typename: comparablesTypename,
    } = last(product?.comparables ?? {}) ?? {};
    let price = '';
    let currency = '';
    if (comparablesTypename === 'CompareElementPrice') {
      price = displayValue;
      currency = prefix;
    }
    return ({
      brand: product?.provider?.providerName ?? '',
      name: product?.productName ?? '',
      id: product?.title ?? '',
      productDetails: product?.comparables?.map(comparablesMappingHandler)?.join('|') ?? '',
      category: product?.__typename ?? '',
      dealStatus: 'true',
      list: toKebabCase(tabTitle) ?? '',
      currency,
      price,
      position: index + 1,
    });
  }) ?? [];

  const trackingData = {
    ecommerce: {
      impressions,
    },
  };
  track({ data: trackingData });
  return trackingData;
}

export function widgetApplyNowTrack(track, tabTitle, product, positionInList) {
  if (
    !track
    || !tabTitle
    || !product
    || positionInList < 0 || positionInList === null || positionInList === undefined
  ) {
    return false;
  }
  const {
    displayValue,
    prefix,
    __typename: comparablesTypename,
  } = last(product?.comparables) ?? {};
  let price = '';
  let currency = '';
  if (comparablesTypename === 'CompareElementPrice') {
    price = displayValue;
    currency = prefix;
  }
  const trackingData = {
    event: 'productClick',
    ecommerce: {
      click:
      {
        actionField:
        {
          action: 'click',
          list: toKebabCase(tabTitle),
        },
        products: [
          {
            brand: product?.provider?.providerName ?? '',
            name: product?.productName ?? '',
            id: product?.title ?? '',
            productDetails: product?.comparables?.map(comparablesMappingHandler)?.join('|') ?? '',
            category: product?.__typename ?? '',
            dealStatus: 'true',
            currency,
            price,
            position: positionInList ?? '',
            promoLabel: product?.promotional?.type.map(t => t.display).join('|') ?? '',
            promotion: product?.promotional?.type.map(t => t.key).join('|') ?? '',
            purchaseIntent: '',
          }],
      },
    },
  };
  track({ data: trackingData });
  return trackingData;
}

const getDataEvent = ({ trackVariables }) => {
  const {
    event, currencyCode, location, actionField, stepDetails, productDetail, ...rest
  } = trackVariables;

  const checkout = Object.assign(
    {}, { actionField }, stepDetails && { stepDetails }, { products: [productDetail] }
  );
  const ecommerce = Object.assign({}, { [event]: checkout }, { currencyCode: currencyCode || '' });
  const trackingData = Object.assign({},
    { event },
    { location },
    productDetail && { ecommerce },
    { ...rest });
  return trackingData;
};

const scbTracking = (props, state, isSubmitted) => {
  if (!props && !state) return false;

  const { template, track, product: { productName, title, provider: { providerName } } } = props;
  const { formState, stepIndex } = state;

  const stepPageVal = formState.steps[stepIndex];
  const trackData = {
    event: 'virtualPage',
    location: 'application page',
    virtualPage: isSubmitted ? template.applicationClosedVirtualPage : stepPageVal.virtualPage,
    provider: providerName,
    product: productName,
    sku: title,
  };

  track({ data: trackData });
  return trackData;
};

const allianzTracking = (props, state, isSubmitted, type) => {
  if (!props && !state) return false;

  const { track, product: { id, productName, provider: { providerName } } } = props;

  const trackData = {
    event: 'login_rewards_freeincentiveapplication_submit',
    location: 'login - rewards free incentive application page',
    intent: 'free incentive application submit',
    dashboardModule: 'rewards',
    products: [{
      name: productName,
      id,
      brand: providerName,
      category: 'online-banking-insurance',
    }],
  };

  if (type === 'onClick') {
    track({ data: trackData });
    return trackData;
  }

  return {};
};

const dbsFormTracking = (props, state, isSubmitted, type, currentStepIndex) => {
  if (!props && !state) return false;
  const { template, applicationKey, track } = props;
  const { formState, stepIndex } = state;
  const stepPageVal = currentStepIndex !== undefined && currentStepIndex !== null
    ? formState.steps[currentStepIndex] : formState.steps[stepIndex];
  const {
    product: { productName, provider: { providerName }, id: productId },
  } = props;

  const productDetail = Object.assign({}, {
    category: 'personal-loan',
    brand: providerName,
    name: productName,
    id: productId,
    price: 0,
  });
  const onLoadData = { location: 'application page' };

  let trackData;
  switch (applicationKey) {
    case 'dbsCustomerLead':
      if (type === 'onLoad') {
        trackData = onLoadData;
      } else {
        trackData = getDataEvent({
          trackVariables: {
            event: 'checkout',
            currencyCode: 'SGD',
            actionField: { step: 1 },
            location: 'application page',
            productDetail,
            stepDetails: {
              stepName: 'lead capture',
              finalStep: false,
              leadCapture: true,
            },
          },
        });
      }
      break;
    case 'dbsPersonalLoan':
      if (type === 'onLoad' && stepPageVal.formStageTag === 'ApplicationDetails') trackData = onLoadData;
      if (type === 'onLoad' && stepPageVal.formStageTag === 'ApplicationComplete') {
        trackData = {
          location: 'application page',
          event: 'virtualPage',
          virtualPageURL: stepPageVal.virtualPage,
        };
      } else if (type === 'onClick' && stepPageVal.formStageTag === 'ApplicationDetails') {
        trackData = getDataEvent({
          trackVariables: {
            event: 'checkout',
            virtualPageURL: isSubmitted
              ? template.applicationClosedVirtualPage : stepPageVal.virtualPage,
            step: stepIndex,
            currencyCode: 'SGD',
            location: 'application page',
            productDetail,
            actionField: { step: 3 },
            stepDetails: {
              stepName: 'application details',
              finalStep: false,
              leadCapture: false,
            },
          },
        });
      } else if (type === 'onClick' && stepPageVal.formStageTag === 'ApplicationComplete') {
        trackData = getDataEvent({
          trackVariables:
          {
            event: 'checkout',
            virtualPageURL: isSubmitted
              ? template.applicationClosedVirtualPage : stepPageVal.virtualPage,
            currencyCode: 'SGD',
            location: 'application page',
            productDetail,
            actionField: { step: 4 },
            stepDetails: {
              stepName: 'application submit',
              finalStep: true,
              leadCapture: false,
            },
          },
        });
      }
      break;
    default:
      trackData = {};
  }

  track({ data: trackData });
  return trackData;
};

const fwdFormTracking = (props, state, isSubmitted, type, currentStepIndex) => {
  if (!props && !state) return false;
  const { track } = props;
  const { formState, stepIndex } = state;
  const stepPageVal = currentStepIndex !== undefined
    ? formState.steps[currentStepIndex] : formState.steps[stepIndex];
  const {
    product: {
      productName, provider: { providerName }, title: productTitle, premium: { totalDisplayPrice },
    },
  } = props;
  const { sections: reviewSections } = formState.steps.find(e => e.stepSlug === 'review');
  const { rows: reviewRows } = reviewSections.find(e => e.sectionSlug === 'Review');
  const { fields: reviewFields } = reviewRows[0];
  const {
    pathToFamilyMembers,
    pathPolicyStartDate,
  } = reviewFields[0]?.customComponentsProps;
  const familyMembersField = ramdaPath(pathToFamilyMembers, formState);
  const coverageStartDateField = ramdaPath(pathPolicyStartDate, formState);
  const productDetail = Object.assign({}, {
    category: 'personal-accident',
    brand: providerName,
    name: productName,
    id: productTitle,
    price: totalDisplayPrice,
    productDetails: {
      numberOfAdults: 1 + filter(e => e.relationship === 'SPOUSE', familyMembersField?.value)?.length,
      numberOfChildren: filter(e => e.relationship === 'CHILD', familyMembersField?.value)?.length,
      coverageStartDate: coverageStartDateField.value,
    },
  });
  const onLoadData = { location: 'application page' };
  const STEPS = ['myDetails', 'review'];
  let trackData;
  if (type === 'onLoad') {
    switch (stepPageVal.formStageTag) {
      case STEPS[0]:
      case STEPS[1]:
        trackData = onLoadData;
        break;
      default:
        break;
    }
  }

  if (type === 'onClick') {
    switch (stepPageVal.formStageTag) {
      case STEPS[0]:
        trackData = getDataEvent({
          trackVariables: {
            event: 'checkout',
            step: stepIndex,
            currencyCode: 'SGD',
            location: 'application page',
            productDetail,
            actionField: { step: 1 },
            stepDetails: {
              stepName: 'application details',
              finalStep: false,
              leadCapture: true,
            },
          },
        });
        break;
      case STEPS[1]:
        trackData = getDataEvent({
          trackVariables:
          {
            event: 'checkout',
            currencyCode: 'SGD',
            location: 'application page',
            productDetail,
            actionField: { step: 2 },
            stepDetails: {
              stepName: 'application review',
              finalStep: true,
              leadCapture: false,
            },
          },
        });
        break;
      default:
        break;
    }
  }
  track({ data: trackData });
  return trackData;
};

const dbsTracking = (props, type) => {
  const { applicationKey, track, status = {} } = props;
  const productTypes = ['creditcard', 'cashline'];
  const getStatus = () => (
    productTypes
      .filter(i => status[i])
      .reduce((obj, f) => (
        Object.assign(
          obj,
          { [f]: status[f].toLowerCase() }
        )
      ), {})
  );

  const getProduct = () => {
    const { product } = props;

    const { productName, provider: { providerName }, id: productId } = product ?? { provider: {} };

    const productDetail = Object.assign({}, {
      category: 'personal-loan',
      brand: providerName,
      name: productName,
      id: productId,
      price: 0,
    });
    return productDetail;
  };

  const getDataEventOnDbsButton = () => {
    const { product, isValidDBS, step } = props;
    const provider = product?.provider?.providerName || '';
    const stepName = isValidDBS ? `${provider} login on singsaver` : `continue on ${provider} website`;
    const trackData = getDataEvent({
      trackVariables: {
        event: 'checkout',
        currencyCode: 'SGD',
        location: 'application page',
        productDetail: getProduct(),
        actionField: { step },
        stepDetails: {
          stepName,
          finalStep: false,
          leadCapture: false,
        },
      },
    });
    return trackData;
  };

  const onLoadData = { location: 'application page' };
  let trackData;
  switch (applicationKey) {
    case 'loginPage':
      if (type === 'onLoad') {
        trackData = onLoadData;
      } else {
        trackData = getDataEventOnDbsButton();
      }
      break;
    case 'applicationConfirmationPage':
      if (type === 'onLoad') {
        trackData = onLoadData;
      } else {
        trackData = getDataEvent({
          trackVariables:
          {
            event: 'purchase',
            currencyCode: 'SGD',
            location: 'application page',
            productDetail: getProduct(),
            actionField: {
              status: Object.values(getStatus()).some(i => i === 'approved') ? 'success' : 'failure',
              response: {
                loanType: getStatus(),
              },
            },
          },
        });
      }
      break;
    case 'errorPage':
      if (type === 'onLoad') {
        trackData = onLoadData;
      } else if (type === 'onClick') {
        if (typeof (props.isValidDBS) !== 'undefined') {
          trackData = getDataEventOnDbsButton();
        } else {
          trackData = getDataEvent({
            trackVariables:
            {
              event: 'returnClick',
              location: 'application page',
              verticalCode: 'PL',
            },
          });
        }
      }
      break;
    default:
      trackData = {};
  }
  track({ data: trackData });
  return trackData;
};

export function applicationFormTracking(
  props,
  state,
  isSubmitted,
  type,
  currentStepIndex,
  countryVal,
) {
  const { provider } = props;
  if (countryVal === 'ss') {
    switch (provider) {
      case 'scb':
        scbTracking(props, state, isSubmitted);
        break;
      case 'dbsx':
        dbsFormTracking(props, state, isSubmitted, type, currentStepIndex);
        break;
      case 'fwd':
        fwdFormTracking(props, state, isSubmitted, type, currentStepIndex);
        break;
      case 'allianzSg':
        allianzTracking(props, state, isSubmitted, type);
        break;
      default:
        break;
    }
  }
  if (countryVal === 'mh') {
    switch (provider) {
      case 'allianzhk':
        allianzTracking(props, state, isSubmitted, type);
        break;
      default:
        break;
    }
  }
}

export function ssApplicationComponentTracking(props) {
  const { provider, type } = props;
  switch (provider) {
    case 'dbs':
    case 'dbsx':
    case 'posb':
      dbsTracking(props, type);
      break;
    default:
      break;
  }
}

const redeemIntents = {
  COMPLETE_APPLICATION_FORM: 'complete application form',
  REDEEM_REWARD_FORM_CONFIRM_CLICK: 'redeem reward form confirm click',
  REDEEM_REWARD_PREVIEW_BACK_CLICK: 'redeem reward preview back click',
  REDEEM_REWARD_PREVIEW_SUBMIT_CLICK: 'redeem reward preview submit click',
};

// RA-6283 - Redemption form load
export function formRewardRedeemOnLoad(track) {
  const trackingData = {
    location: LOCATIONS.REWARDS_REDEMPTION_PAGE,
  };
  return track({ data: trackingData });
}

// RA-6283:
// - Redemption Application text link
// - Redemption Confirm CTA
// - Redemption Preview Back
// - Redemption Preview Submit
export function formRewardRedeemTracking(track, trackingId, product, campaign) {
  const intent = redeemIntents[trackingId];
  const location = LOCATIONS.REWARDS_REDEMPTION_PAGE;
  const dashboardModule = 'rewards';
  let trackingData = {};
  if (intent) {
    trackingData = {
      event: 'login_rewards_redeem',
      location,
      intent,
      dashboardModule,
    };
  }
  if (trackingId === 'REDEEM_REWARD_PREVIEW_SUBMIT_CLICK') {
    trackingData.verticalCode = VERTICAL_CODE_FROM_PRODUCT_TYPENAME[product?.__typename];
    trackingData.products = [
      {
        name: product?.productName ?? '',
        brand: product?.provider?.providerName ?? '',
        id: product?.title ?? '',
        price: '',
        category: product?.__typename ?? '',
        rewardCampaignName: campaign?.header ?? '',
      },
    ];
  }
  track({ data: trackingData });
  return trackingData;
}

// RA-6283 - Redemption Help text link
export function formRewardRedeemHelpText(track, helpText) {
  const trackingData = {
    event: 'login_rewards_redeem',
    location: LOCATIONS.REWARDS_REDEMPTION_PAGE,
    intent: helpText?.toLowerCase(),
    dashboardModule: 'rewards',
  };
  return track({ data: trackingData });
}

// RA-6283 - ThankYou load
export function rewardThankYouModalOnLoad(track, product, campaign) {
  const trackingData = {
    location: LOCATIONS.REWARDS_PAGE,
    virtualPageURL: `${getBaseURL()}/${VIRTUAL_PAGES_URL.REWARDS_THANK_YOU}`,
    event: 'virtualPage',
    dashboardModule: 'rewards',
    intent: 'submitted',
    verticalCode: VERTICAL_CODE_FROM_PRODUCT_TYPENAME[product?.__typename],
    products: [
      {
        name: product?.productName ?? '',
        brand: product?.provider?.providerName ?? '',
        id: product?.title ?? '',
        price: '',
        category: product?.__typename ?? '',
        rewardCampaignName: campaign?.header ?? '',
      },
    ],
  };
  return track({ data: trackingData });
}

// RA-6283 - Redemption Success load
export function rewardRedeemThankYouModalOnLoad(track) {
  const trackingData = {
    location: LOCATIONS.REWARDS_REDEMPTION_PAGE,
    virtualPageURL: `${getBaseURL()}/${VIRTUAL_PAGES_URL.REWARDS_FORM_SUCCESS}`,
    event: 'virtualPage',
  };
  return track({ data: trackingData });
}

// RA-6283 - ThankYou Continue button
export function rewardThankYouModalCta(track) {
  const trackingData = {
    location: LOCATIONS.REWARDS_PAGE,
    virtualPageURL: `${getBaseURL()}/${VIRTUAL_PAGES_URL.REWARDS_THANK_YOU}`,
    intent: 'continue to apply',
    event: 'login_rewards_redeem',
  };
  return track({ data: trackingData });
}

// RA-6283 - Redmpetion Success to Dashboard
export function rewardRedeemThankYouModalCta(track) {
  const trackingData = {
    location: LOCATIONS.REWARDS_REDEMPTION_PAGE,
    virtualPageURL: `${getBaseURL()}/${VIRTUAL_PAGES_URL.REWARDS_FORM_SUCCESS}`,
    event: 'returnClick',
  };
  return track({ data: trackingData });
}

// RA-6283 - CampaignLP Skip reward button & Signup/Login Skip reward button
export function rewardHeaderSkipReward(track, page, product) {
  if (typeof page === 'undefined' || !track || (page === 'register' && !product)) {
    return false;
  }
  const trackingData = {};
  if (page === 'landing') {
    trackingData.location = LOCATIONS.REWARDS_LANDING_PAGE;
    trackingData.event = 'login_rewards_redeem';
    trackingData.intent = 'skip reward';
  }
  if (page === 'register') {
    trackingData.location = LOCATIONS.SIGNUP;
    trackingData.event = 'login_rewards_redeem';
    trackingData.intent = 'skip reward';
    trackingData.verticalCode = VERTICAL_CODE_FROM_PRODUCT_TYPENAME[product?.__typename] ?? '';
    trackingData.virtualPageURL = `${getBaseURL()}/signup`;
  }
  return track({ data: trackingData });
}

// RA-6283 - CampaignLP page load
export function rewardLandingOnLoad(track, product, campaign) {
  const trackingData = {
    location: LOCATIONS.REWARDS_LANDING_PAGE,
    verticalCode: VERTICAL_CODE_FROM_PRODUCT_TYPENAME[product?.__typename],
    products: [
      {
        name: product?.productName ?? '',
        brand: product?.provider?.providerName ?? '',
        id: product?.title ?? '',
        price: '',
        category: product?.__typename ?? '',
        rewardCampaignName: campaign?.header ?? '',
      },
    ],
  };
  return track({ data: trackingData });
}

// RA-6283 - CampaignLP Continue button
export function rewardLandingContinueCTA(track) {
  const trackingData = {
    location: LOCATIONS.REWARDS_LANDING_PAGE,
    componentType: 'button',
    event: 'signupNow',
  };
  return track({ data: trackingData });
}

// RA-6283 - RewardConf on load
export function rewardConfirmModalOnLoad(track) {
  const trackingData = {
    location: LOCATIONS.REWARDS_PAGE,
    virtualPageURL: `${getBaseURL()}/${VIRTUAL_PAGES_URL.REWARDS_SKIP}`,
    intent: 'skip reward',
    event: 'virtualPage',
  };
  return track({ data: trackingData });
}

// RA-6283 - RewardConf Skip reward CTA
export function rewardConfirmModalCTA(track) {
  const trackingData = {
    location: LOCATIONS.REWARDS_PAGE,
    virtualPageURL: `${getBaseURL()}/${VIRTUAL_PAGES_URL.REWARDS_SKIP}`,
    intent: 'skip reward',
    event: 'login_rewards_redeem',
  };
  return track({ data: trackingData });
}

// RA-6283 - ThankYou X button
export function rewardThankYouModalClose(track) {
  const trackingData = {
    location: LOCATIONS.REWARDS_PAGE,
    virtualPageURL: `${getBaseURL()}/${VIRTUAL_PAGES_URL.REWARDS_THANK_YOU}`,
    componentType: 'button',
    action: 'click',
    title: 'close',
    event: 'navigate',
  };
  return track({ data: trackingData });
}

// RA-6283 - Redemption Success X button
export function rewardRedeemThankYouModalClose(track) {
  const trackingData = {
    location: LOCATIONS.REWARDS_REDEMPTION_PAGE,
    virtualPageURL: `${getBaseURL()}/${VIRTUAL_PAGES_URL.REWARDS_FORM_SUCCESS}`,
    componentType: 'button',
    action: 'click',
    title: 'close',
    event: 'navigate',
  };
  return track({ data: trackingData });
}

// RA-6283 - RewardConf 'Want Reward'
export function rewardConfirmModalWant(track, ctaText) {
  const trackingData = {
    location: LOCATIONS.REWARDS_PAGE,
    virtualPageURL: `${getBaseURL()}/${VIRTUAL_PAGES_URL.REWARDS_SKIP}`,
    intent: ctaText,
    event: 'login_rewards_redeem',
  };
  return track({ data: trackingData });
}

// RA-6283 - RewardConf X button
export function rewardConfirmModalClose(track) {
  const trackingData = {
    location: LOCATIONS.REWARDS_PAGE,
    virtualPageURL: `${getBaseURL()}/${VIRTUAL_PAGES_URL.REWARDS_SKIP}`,
    componentType: 'button',
    action: 'click',
    title: 'close',
    event: 'navigate',
  };
  return track({ data: trackingData });
}

// RA-6283 - Error page load
export function rewardGenericErrorOnLoad(track) {
  const trackingData = {
    location: LOCATIONS.ERROR,
  };
  return track({ data: trackingData });
}

// RA-6283 - Error page Back CTA
export function rewardGenericErrorCTA(track, ctaText) {
  const trackingData = {
    location: LOCATIONS.ERROR,
    event: 'returnClick',
    title: ctaText,
  };
  return track({ data: trackingData });
}

export function whitepaperTrackingEvent(track, action, title, position, componentType) {
  track({
    data: {
      event: 'navigate',
      location: 'login - whitepaper',
      componentType,
      action,
      title,
      position,
    },
  });
}

export function navBarFooterTrackingEvent(track, action, title, position, componentType) {
  const data = {
    event: 'navigate',
    componentType,
    action,
    title,
    position,
  };
  track({
    data,
  });
  return data;
}

export function subscribeBtnTracking(track, page, component) {
  if (!track) return false;

  const pageDictionary = {
    home: 'homepage',
    provider: 'provider page',
    static: 'static page',
  };
  const location = pageDictionary[page];
  const trackingData = {
    event: 'subscribeClick',
    componentType: component ?? 'before-footer',
    location: location ?? 'generic page',
  };
  track({
    data: trackingData,
  });
  return trackingData;
}
